<template>
  <div id="map" data-test="map">
    <div v-if="mapboxNotSupported">
      <mapbox-supported-modal @close="mapboxNotSupported = false" />
    </div>
    <div id="viewer-container" ref="viewerContainer" />
    <slot />
  </div>
</template>

<!-- eslint-disable vue/block-lang -->
<script>
/* eslint-disable vue/component-api-style */
// dependency imports
import { useTemplateRef } from 'vue'
import { captureException } from '@sentry/vue'
import { error } from '@/lib/logger'
import db from 'lodash/debounce'
import throttle from 'lodash/throttle'
import { supported, notSupportedReason } from '@mapbox/mapbox-gl-supported'
// service and lib imports
import * as ms from '@/services/map-service'
import { isWaypointClusteringEnabled } from '@/lib/waypoint-clustering'

// internal viewer functions
/* eslint-disable no-restricted-imports */
import * as vc from '@/lib/map-viewer/viewer-camera'
import * as vi from '@/lib/map-viewer/viewer-images'
import * as ve from '@/lib/enums'
import * as vn from '@/lib/map-viewer/viewer-interaction'
import * as vf from '@/lib/map-viewer/viewer-features'
import * as vl from '@/lib/map-viewer/viewer-layers'
import * as vr from '@/lib/map-viewer/viewer-report'
import * as vt from '@/lib/map-viewer/viewer-telemetry'
import mapboxSupportedModal from '@/components/map-viewer/mapbox-supported-modal'
import { addDebugContextMenu } from '@/components/map-controls/control-context-dev-debug'
import { initMapTimingPerformanceMetrics } from '@/lib/map-viewer/map-performance'
import { initMarkupRenderTimingPerformanceMetrics } from '@/services/markup-service/markup-performance'
import { initializeFeaturePopup } from '@/components/map-viewer/feature-popup'
import { removeUser } from '@/services/identity-client'
import { setLayerManagementReady } from '@/services/layer-management-service'
import { useWaypointClustering } from '@/composables/waypoint-clustering'
import { useMapManager } from '@/composables/map-x/use-map-manager'
import {
  getMaxPitch,
  shouldDisable3d,
} from '@/services/map-provider/map-config'

/** @type {import("@onxmaps/map-x").MapManager} */
let mapManager = null
/** @type {import("@/lib/map-x/webmap-viewer-types").WebmapMapViewer} */
let mapViewer = null

/**
 * Mapbox map-viewer facade component: https://docs.mapbox.com/mapbox-gl-js
 */
export default {
  name: 'MapViewer',
  components: {
    mapboxSupportedModal,
  },
  props: {
    featurePopoverSources: { type: Object, default: null },
    features: { type: Object, default: null },
    drawManager: { type: Function, default: () => {} },
    layerOpacities: { type: Object, default: () => ({}) },
    layerFilters: { type: Object, default: () => ({}) },
    shownLayers: { type: Object, default: () => ({}) },
    stylesheetSources: { type: Object, default: () => ({}) },
    useBaseCollisionBox: { type: Boolean, default: false },
  },

  emits: ['map-event', 'map-click', 'map-long-press', 'fps-measured'],

  setup() {
    const viewerContainer = useTemplateRef('viewerContainer')
    // waypoint clustering
    const { toggleClusteringViaMapSetting, toggleClusteringOnLoad } =
      useWaypointClustering()

    // Get the MapManager from the injection - this must be done in setup()
    const injectedMapManager = useMapManager()

    return {
      viewerContainer,
      toggleClusteringViaMapSetting,
      toggleClusteringOnLoad,
      injectedMapManager,
      shouldDisable3d,
    }
  },

  data() {
    return {
      mapboxNotSupported: false,
      // eslint-disable-next-line vue/no-unused-properties
      markupsLoaded: false,
    }
  },

  computed: {
    userZoom: ms.userZoom,
    destination: ms.destination,
    quickDestination: ms.quickDestination,
    camera: ms.camera,
    queryCoord: ms.queryCoord,
    fitFeature: ms.fitFeature,
    fitBounds: ms.fitBounds,
    featureSelected: ms.featureSelected,
    featureTargeted: ms.featureTargeted,
    featuresHovered: ms.featuresHovered,
    featuresFavorited: ms.featuresFavorited,
    featuresFiltered: ms.featuresFiltered,
    searchFeatureSelected: ms.searchPinSelected,
    userCoord: ms.userCoord,
    cursor: ms.cursor,
    terrainExaggeration: ms.terrainExaggeration,
    canPitch: ms.canPitch,
    canRotate: ms.canRotate,
    popup: ms.popup,
    markers: ms.markers,
    images: ms.images,
    viewer3d: ms.viewer3d,
  },

  watch: {
    shownLayers: {
      handler: async function (newVal, oldVal) {
        await ms.mapReadyPromise
        vl.shownLayers(mapViewer.map, newVal, oldVal)
      },
      immediate: true,
    },

    layerOpacities: {
      handler: throttle(async function (newVal, oldVal) {
        await ms.mapReadyPromise
        vl.setLayerOpacities(mapViewer.map, newVal, oldVal)
      }, 50),
      immediate: true,
    },

    layerFilters: {
      handler: throttle(async function (newVal, oldVal) {
        await ms.mapReadyPromise
        vl.setLayerFilters(mapViewer.map, newVal, oldVal)
      }, 50),
      immediate: true,
    },

    async stylesheetSources(newVal) {
      await ms.mapReadyPromise
      vl.setNewSources(mapViewer.map, newVal)
    },

    async popup(val) {
      await ms.mapReadyPromise
      vf.setPopup(mapViewer, val)
    },
    async markers(newVal) {
      await ms.mapReadyPromise
      vf.updateMarkers(mapViewer, newVal)
    },
    camera: db(function (newVal) {
      vc.saveLastView(newVal)
    }, 100),
    async userZoom(newVal) {
      await ms.mapReadyPromise
      vc.updateZoom(mapViewer.map, newVal)
    },
    async fitFeature(newVal) {
      await ms.mapReadyPromise
      if (newVal?.feature) {
        const { feature, bearing, pitch, maxZoom, minZoom, padding } = newVal
        vc.fitFeature_(
          mapViewer.map,
          feature,
          bearing,
          pitch,
          maxZoom,
          minZoom,
          padding
        )
      }
    },
    async fitBounds(newVal) {
      await ms.mapReadyPromise
      vc.fitBounds_(mapViewer.map, newVal)
    },
    async userCoord(newVal) {
      await ms.mapReadyPromise
      vf.setUserMarker(mapViewer, newVal)
    },
    async featureSelected(newVal) {
      await ms.mapReadyPromise
      vf.setFeatureSelected(mapViewer.map, newVal)
    },
    async featureTargeted(newVal) {
      await ms.mapReadyPromise
      vf.setFeatureTargeted(mapViewer.map, newVal)
    },
    async searchFeatureSelected(newVal) {
      await ms.mapReadyPromise
      vf.setSearchFeatureSelected(mapViewer.map, newVal)
    },
    async destination(newVal) {
      await ms.mapReadyPromise
      vc.updateCamera(mapViewer.map, newVal)
    },
    async quickDestination(newVal) {
      await ms.mapReadyPromise
      vc.updateCameraQuick(mapViewer.map, newVal)
    },
    queryCoord: {
      handler: async function (newVal) {
        await ms.mapReadyPromise
        vf.setQueryFeatures(mapViewer, newVal, this.useBaseCollisionBox)
      },
      immediate: true,
    },
    async terrainExaggeration(newVal, oldVal) {
      await ms.mapReadyPromise
      this.adjustTerrainExaggeration(mapViewer, newVal, oldVal)
    },
    async viewer3d(is3d) {
      await ms.mapReadyPromise
      this.enable3d(mapViewer, is3d)
    },
    async cursor(cursor) {
      await ms.mapReadyPromise
      mapViewer.map.getCanvas().style.cursor = cursor
    },
    featuresHovered(val) {
      vn.handleSelectableCursor(mapViewer.map, val)
      vf.setFeaturesStateHovered(mapViewer.map, val)
    },
    featuresFavorited(val) {
      vf.setFeaturesStateFavorited(mapViewer.map, val)
    },
    async featuresFiltered(newVal) {
      await ms.mapReadyPromise
      vf.setFeaturesStateFiltered(mapViewer.map, newVal)
    },
    canPitch(val) {
      vc.toggleAbilityToPitchMap(mapViewer, val, this.shouldDisable3d)
    },
    canRotate(val) {
      vc.toggleAbilityToRotateMap(mapViewer.map, val)
    },
    shouldDisable3d(isDisabled) {
      mapViewer.setMaxPitch(getMaxPitch(isDisabled))
    },
  },

  // eslint-disable-next-line require-await
  async mounted() {
    this.loadMap()
  },

  beforeUnmount() {
    ms.setReady(false)
    setLayerManagementReady(false)
    vf.unwatchFeatureWatchers()
  },

  unmounted() {
    mapViewer?.map?.remove()
  },

  methods: {
    setMapReady() {
      // trigger setting inital state
      mapManager.events.emit('map-x-camera-move')
      // ensures the map canvas fits its container
      mapViewer?.map?.resize()
      // service ready state
      ms.setReady(true)
    },
    checkMapboxSupported() {
      const isSupported = supported()

      if (!isSupported) {
        const reason = notSupportedReason()

        captureException(new Error('mapbox not supported'), {
          tags: {
            mapbox: 'not supported',
          },
          extra: {
            reason,
          },
        })

        this.$emit('map-event', {
          type: 'event',
          name: 'mapbox_not_supported',
          reason,
        })

        this.mapboxNotSupported = true
      }
      return isSupported
    },
    async loadMap() {
      // ensure viewerContainer can exist before using the ref
      await this.$nextTick()

      // check mapbox is supported
      this.checkMapboxSupported()

      // Get the MapManager that was provided by useMapManager
      mapManager = this.injectedMapManager

      if (!mapManager) {
        const errorMessage =
          'MapManager not found - provideMapManager or useMapManager may not be working correctly'
        error('Map', errorMessage)
        captureException(new Error(errorMessage), {
          tags: { map: 'map-x' },
        })
        return
      }

      const containerElement = this.viewerContainer

      if (!containerElement) {
        const errorMessage = 'viewer-container element not found'
        error('Map', errorMessage)
        captureException(new Error(errorMessage), {
          tags: { map: 'map-x' },
        })
        return
      }

      // Append the MapManager's container into the host element
      containerElement.appendChild(mapManager.container)

      try {
        await mapManager.makeReady()
      } catch (err) {
        const errorMessage = `mapManager.makeReady() failed`
        error('Map', errorMessage, err)
        captureException(new Error(errorMessage), {
          originalException: { err },
          tags: { map: 'map-x' },
        })
      }

      // Wait for the stylesheet to be fully loaded on the mapbox instance
      // This ensures map.style is available when we access it
      await new Promise((resolve) => {
        mapManager.events.once('map-x-stylesheet-loaded', resolve)
      })

      // Get the underlying viewer instance
      mapViewer = mapManager.viewer

      // Guard: if viewer or map are missing after makeReady(), the initialization
      // has genuinely failed — return early rather than propagating null-reference errors.
      if (!mapViewer) {
        const errorMessage = `mapViewer is undefined after mapManager.makeReady()`
        error('Map', errorMessage)
        captureException(new Error(errorMessage), { tags: { map: 'map-x' } })
        return
      }
      if (!mapViewer.map) {
        const errorMessage = `mapViewer.map is undefined after mapManager.makeReady()`
        error('Map', errorMessage)
        captureException(new Error(errorMessage), { tags: { map: 'map-x' } })
        return
      }

      if (typeof mapViewer.loadLibraryCSS === 'function') {
        mapViewer.loadLibraryCSS()
      }

      initMapTimingPerformanceMetrics(mapManager, this.$emit)
      initMarkupRenderTimingPerformanceMetrics(mapManager)
      this.drawManager(mapManager)

      // event listeners
      mapManager.events.once('map-x-load', () => {
        vf.setFeatureWatchers(mapViewer.map, this.features, this.$emit)
        vi.loadImages(mapViewer)
        this.setMapReady()
      })
      mapManager.events.once('map-x-idle', () =>
        this.$emit('map-event', {
          type: 'event',
          name: 'main_activity_started',
          network: 'wifi',
        })
      )
      mapManager.events.on('map-x-style-image-missing', (missingImage) => {
        // If there is a missing image try to load it from the images map
        if (!missingImage.detail) {
          return
        }
        const { id } = missingImage.detail
        // Only load the image asked for
        if (this.images.has(id)) {
          const { image, options } = this.images.get(id)
          mapViewer.addImage(id, image, options)
        }
      })
      mapViewer.map.on('mousemove', (e) => vn.setCursorCoord(e))
      mapManager.events.on('map-x-camera-move', () => {
        try {
          vc.setVisibleRegion(mapViewer)
        } catch (err) {
          const errorMessage = `Error setting visible region on map move: ${err.message}`
          error('Map', errorMessage)
          captureException(new Error(errorMessage), {
            tags: { map: 'map-x' },
          })
        }
      })
      mapManager.events.on('map-x-camera-move', async () => {
        try {
          // We want to wait until the map has loaded because the setting of the camera
          // will cause the setting of the maps terrain (we use the camera to determine the exaggeration)
          await ms.mapReadyPromise
          vc.setCamera(mapViewer)
        } catch (err) {
          // Ignore projection errors when canvas has invalid dimensions
        }
      })
      mapManager.events.on('map-x-camera-moveend', () => vt.moveEnd())
      mapManager.events.on('map-x-render-start', vr.handleRenderStart)
      mapManager.events.on('map-x-render', () => vr.reportRender(this.$emit))
      mapManager.events.on(
        'map-x-render',
        db(() => {
          vf.setFeaturesInView(mapViewer)
        }, 250)
      )
      /** @type {import("@onxmaps/map-x/events").EventCallback<'map-x-viewer-error'>} */
      mapManager.events.on('map-x-viewer-error', this.handleMapTileLoadError)
      vn.addFeatureEventHandlers(
        mapViewer,
        this.$emit,
        this.useBaseCollisionBox
      )
      initializeFeaturePopup({
        mapViewer,
        sources: this.featurePopoverSources,
      })
      addDebugContextMenu(mapViewer)
      if (isWaypointClusteringEnabled.value) {
        this.toggleClusteringOnLoad(mapManager)
        this.toggleClusteringViaMapSetting(mapManager)
      }
    },
    /** @type {import("@onxmaps/map-x/events").EventCallback<'map-x-viewer-error'>} */
    async handleMapTileLoadError(event) {
      if (event?.detail?.raw?.error?.status === 401) {
        await removeUser({ forUnauthorizedRequest: true })
        location.reload()
      }
    },

    enable3d(mapViewer, enabled) {
      const exaggeration = ms.terrainExaggeration()

      if (enabled) {
        this.adjustTerrainExaggeration(mapViewer, exaggeration, 0)
      } else {
        this.adjustTerrainExaggeration(mapViewer, 0, exaggeration)
      }
    },
    adjustTerrainExaggeration(mapViewer, newExag, oldExag) {
      let terrainOptions = null

      if (newExag !== 0) {
        terrainOptions = { exaggeration: newExag }
        if (oldExag === 0) {
          // Only set source when entering 3D mode
          terrainOptions.source = ve.TERRARIUM_DEM_SOURCE
        }
      }

      // Use setTerrain abstraction to set terrain exaggeration
      mapViewer.setTerrain(terrainOptions)

      // Don't adjust the camera if moving in/out of 3D
      if (newExag === 0 || oldExag === 0) {
        return
      }

      // set new camera height so as the terrain exaggeration is adjusted
      // the camera does not "collide" with the map
      vn.adjustCameraHeightForExaggeration(mapViewer, newExag, oldExag)
    },
  },
}
</script>

<!-- eslint-disable vue/enforce-style-attribute -->
<style lang="scss">
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_animation.scss';
@use '@/styles/_variables.scss';
@use '@/styles/_layer-map.scss';
@use '@/styles/system/type';

#map {
  position: relative;
  overflow: hidden;
  z-index: layer-map.$z-index-level-map;

  #viewer-container {
    height: 100%;
    width: 100%;

    // this can be removed when/if bitters css is removed from webmap
    .mapboxgl-ctrl-attrib {
      box-sizing: initial;
    }
  }

  .user-location-marker {
    border-radius: 50%;
    height: 16px;
    width: 16px;
    background-color: colors.$BaseBlue;
    border: 2px solid colors.$BaseWhite;
    box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.5);

    &.center {
      transform: translate(-50%, -50%);
    }
  }

  .bulk-select-box {
    background: rgb(226, 226, 226, 0.2);
    border: 3px solid var(--ys-color-surface-primary);
    border-style: dashed;
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
  }
}

.mapboxgl-marker {
  position: absolute;
  top: 0;
  left: 0;
  will-change: transform;
  opacity: 1;
  transition: opacity animation.$timing_250 animation.$ease_standard;

  &.search-result-marker {
    opacity: 1 !important;
  }
}

.mapboxgl-canvas-container.mapboxgl-interactive {
  cursor: auto;
}

a.mapboxgl-ctrl-logo {
  transform: scale(0.8);
  margin: 0 0 -5px -15px;
  pointer-events: none;

  &::after {
    content: '';
    pointer-events: auto;
    width: 23px;
    height: 23px;
    display: block;
  }
}

@media print {
  .mapboxgl-ctrl-bottom-left,
  .controls-bottom-right,
  .side-panel {
    display: none;
  }
}
</style>
